import { useCallback, useEffect, useState } from 'react';
import * as Y from 'yjs';

export function useYDocText(yDoc: Y.Doc, name: string) {
    const docText = yDoc.getText(name);
    const [ value, setValue ] = useState(docText.toJSON());

    const updateValue = useCallback((newValue: string) => {
        docText.delete(0, docText.length);
        docText.insert(0, newValue);
    }, [ docText ]);

    useEffect(() => {
        const handleChange = () => {
            setValue(docText.toJSON());
        };

        docText.observe(handleChange);

        return () => {
            docText.unobserve(handleChange);
        };
    }, [ docText ]);

    return [value, updateValue] as const;
}

export function useYDocMap<T>(yDoc: Y.Doc, name: string) {
    const docMap = yDoc.getMap<T>(name);
    const [ entries, setEntries ] = useState(() => Array.from(docMap.entries()));
    const [ docMapRef, setDocMapRef ] = useState(() => ({ ref: docMap }));

    const addEntry = useCallback((newKey: string, newValue: T) => {
        docMap.set(newKey, newValue);
    }, [ docMap ]);

    const updateEntry = useCallback((key: string, newValue: T) => {
        docMap.set(key, newValue);
    }, [ docMap ]);

    const removeEntry = useCallback((key: string) => {
        docMap.delete(key);
    }, [ docMap ]);

    useEffect(() => {
        const handleChange = () => {
            setEntries(Array.from(docMap.entries()));
            setDocMapRef({ ref: docMap });
        };

        docMap.observe(handleChange);

        return () => {
            docMap.unobserve(handleChange);
        };
    }, [ docMap ]);

    return {
        entries,
        docMap: docMapRef.ref,
        addEntry,
        updateEntry,
        removeEntry
    };
}
